import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

// ─────────────────────────────────────────────────────────────
// PATH NOTE
// Written for the same folder as ElectronicsStore / GroceryStore /
// KitchenStore (four levels deep). If you drop it beside
// StoreProductsHome instead, remove one "../" from every import.
// ─────────────────────────────────────────────────────────────
import type { AppDispatch, RootState } from "../../../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../../redux/storeCartSlice";
import { fetchAllCommercePartners } from "../../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../../redux/storeProductRatingSlice";
import { logout } from "../../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../../GLOBAL/CurrencyContext";
import appLogo from "../../../../assets/app/logo.png";

/* ⭐ CATEGORY SOURCE — the same collection StoreProducts saves with.
   This page is the Luggage department and nothing else: the rail
   toggles Luggage's own categories, and Shop by Category drills
   category → subCategory → productType → product. */
import {
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, X, ChevronRight, ChevronDown, Plus, Minus, Check,
  Heart, User, ShoppingCart, MapPin, Globe, AlertTriangle, LogOut,
  Luggage, Plane, Ruler, Weight, LayoutGrid, Home, Package, Star,
  ShoppingBag, ShoppingBasket, Sliders, CircleDot, Lock, RotateCcw,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE
═══════════════════════════════════════════════════════════ */
const LUGGAGE_DEPT_ID = "13";

/* Cabin allowance used for the fit check. Generous on purpose —
   55×40×20 is the strictest common rule, 56×45×25 covers most
   carriers. The modal always shows the numbers we tested against
   so nobody takes it as gospel. */
const CABIN_LIMIT = { long: 56, wide: 45, deep: 25 };

/* ═══════════════════════════════════════════════════════════
   DESIGN — airport signage, not a fashion lookbook.

   Four colours, no more: NAVY for ink and the board, ORANGE for
   anything a hand touches, PAPER for the page, ALU for the ribbed
   shell and every border. The signature is the baggage tag looped
   over each photo, and it earns its place: it carries the one fact
   that decides the purchase — cabin or hold.
═══════════════════════════════════════════════════════════ */
const NAVY = "#16293D";
const ORANGE = "#E4572E";
const PAPER = "#F6F5F1";
const ALU = "#C9CFD5";

const L = {
  navy: NAVY,
  navySoft: "#33475C",
  orange: ORANGE,
  orangeSoft: "#FCEAE3",
  orangeEdge: "#F2BCA8",
  paper: PAPER,
  sheet: "#FFFFFF",
  sheet2: "#FAFAF8",
  alu: ALU,
  line: "#E3E4E0",
  lineSoft: "#EEEEEA",
  ink: NAVY,
  inkSoft: "#3E4C5A",
  muted: "#71787F",
  faint: "#A0A6AB",
};

/* the seller's colour is the only accent the page doesn't own */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || NAVY;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry always resolved by walking
   parentId, never by string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Path with the department stripped — everything here is Luggage. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).filter((cid) => cid !== LUGGAGE_DEPT_ID).map(catName).join(" / ");

/** Luggage's own categories — the rail and the icon bar. */
const LUGGAGE_CATEGORIES: CategoryItem[] = childrenOf(LUGGAGE_DEPT_ID);

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — same bridge StoreProducts uses.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain].reverse().find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const key = String(categoryId);
  const hit = attrCache.get(key);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [toAttributeCategoryId(categoryId), ...chain.flatMap((c) => [`0${c}`, c])].filter(Boolean);

  let found: any[] = [];
  for (const k of keys) {
    try {
      const defs = attributesForCategory(k as any);
      if (defs && defs.length) { found = defs; break; }
    } catch { /* unknown key — keep walking up */ }
  }
  attrCache.set(key, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Attributes whose options make them a buyable variant (Colour, Size…). */
const isVariantAttr = (attr: any) =>
  (attr?.inputType === "select" || !attr?.inputType) &&
  Array.isArray(attr?.options) && attr.options.length > 0;

const variantAttrsForItem = (item: any) => resolveAttributes(item?.categoryId).filter(isVariantAttr);
const hasVariants = (item: any) => variantAttrsForItem(item).length > 0;

interface Fact { name: string; value: string }
/** Everything the seller actually filled in. */
const factsOf = (item: any): Fact[] => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .filter((def: any) => !isEmptyValue(saved[String(def.id)]))
    .map((def: any) => ({ name: def.name, value: resolveAttrValue(def, saved[String(def.id)]) }))
    .filter((f) => f.value);
};

const firstFact = (item: any, re: RegExp): Fact | null => {
  const saved = item?.attributes ?? {};
  const def = resolveAttributes(item?.categoryId)
    .find((d: any) => re.test(d.name || "") && !isEmptyValue(saved[String(d.id)]));
  if (!def) return null;
  const value = resolveAttrValue(def, saved[String(def.id)]);
  return value ? { name: def.name, value } : null;
};

/* Luggage is bought on four numbers and one question:
   how big, how heavy, how much it holds — and will it go in the cabin. */
const DIM_RE = /dimension|measure|size|external|h ?x ?w|w ?x ?d/i;
const WEIGHT_RE = /weight|mass|empty/i;
const CAPACITY_RE = /capacity|volume|litre|liter|contents/i;
const SHELL_RE = /shell|material|hard|soft|fabric|polycarbonate|alumin/i;
const WHEELS_RE = /wheel|spinner|castor|roller/i;
const LOCK_RE = /lock|tsa|secure/i;
const CABIN_RE = /cabin|carry.?on|hand.?luggage|underseat/i;

const weightFactOf = (item: any) => firstFact(item, WEIGHT_RE);
const capacityFactOf = (item: any) => firstFact(item, CAPACITY_RE);
const shellFactOf = (item: any) => firstFact(item, SHELL_RE);
const wheelsFactOf = (item: any) => firstFact(item, WHEELS_RE);

const hasLock = (item: any) => {
  const f = firstFact(item, LOCK_RE);
  return !!f && !/^(no|false|none)$/i.test(f.value.trim());
};

/** "55 × 40 × 20 cm" → the three numbers, largest first. */
const parseDims = (raw?: string | null): number[] | null => {
  if (!raw) return null;
  const nums = String(raw).match(/\d+(?:[.,]\d+)?/g);
  if (!nums || nums.length < 3) return null;
  const three = nums.slice(0, 3).map((n) => parseFloat(n.replace(",", ".")));
  if (three.some((n) => !isFinite(n) || n <= 0)) return null;
  return three.sort((a, b) => b - a);
};

type CabinClass = "cabin" | "hold" | null;

/** Only ever answers from what the seller supplied — never guesses. */
const cabinClassOf = (item: any): CabinClass => {
  const dims = parseDims(firstFact(item, DIM_RE)?.value);
  if (dims) {
    const [long, wide, deep] = dims;
    return long <= CABIN_LIMIT.long && wide <= CABIN_LIMIT.wide && deep <= CABIN_LIMIT.deep
      ? "cabin" : "hold";
  }
  const flag = firstFact(item, CABIN_RE);
  if (flag && !/^(no|false|none)$/i.test(flag.value.trim())) return "cabin";
  return null;
};

/** "55 × 40 × 20 cm · 2.6 kg · 38 L" — whatever exists. */
const specLineOf = (item: any): string | null => {
  const d = firstFact(item, DIM_RE);
  const w = weightFactOf(item);
  const c = capacityFactOf(item);
  const parts: string[] = [];
  if (d) parts.push(d.value);
  if (w) parts.push(w.value);
  if (c) parts.push(c.value);
  return parts.length ? parts.join(" · ") : null;
};

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
type FitKey = "all" | "cabin" | "hold";

const isOutOfStock = (item: any) => item?.available === false || (item?.stock ?? 0) <= 0;

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null =>
  country ? COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null : null;
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700&family=Barlow:wght@400;500;600;700&family=Roboto+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-thumb { background: ${L.alu}; border-radius: 99px; }
input, button, select { font-family: inherit; }
.lg :focus-visible { outline: 2px solid ${ORANGE}; outline-offset: 2px; border-radius: 6px; }

@keyframes lgIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes lgBlink { 0%,100% { opacity: 1; } 50% { opacity: .3; } }
@keyframes lgSwing { 0%,100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }

.lg { min-height: 100vh; background: ${L.paper}; color: ${L.ink};
  font-family: 'Barlow', sans-serif; padding-bottom: 96px; position: relative; }
.lg-mono { font-family: 'Roboto Mono', monospace; font-variant-numeric: tabular-nums; }
.lg-disp { font-family: 'Barlow Condensed', 'Barlow', sans-serif; font-weight: 700;
  text-transform: uppercase; letter-spacing: .01em; }

/* ── header ── */
.lg-rail { position: sticky; top: 0; z-index: 200;
  background: rgba(246,245,241,.94); backdrop-filter: blur(14px);
  border-bottom: 1px solid ${L.line}; }
.lg-edge { height: 3px; background: ${NAVY}; }

.lg-hbtn { display:flex; align-items:center; gap:6px; background:${L.sheet};
  border:1px solid ${L.line}; color:${L.inkSoft}; font-size:12px; font-weight:600;
  padding:9px 13px; border-radius:6px; cursor:pointer; flex-shrink:0; white-space:nowrap;
  transition: border-color .16s, color .16s, background .16s; }
.lg-hbtn:hover { border-color:${L.alu}; background:${L.sheet2}; color:${L.ink}; }

.lg-search { display:flex; align-items:center; flex:1; min-width:0; background:${L.sheet};
  border:1px solid ${L.line}; border-radius:6px; overflow:hidden; transition:border-color .16s; }
.lg-search:focus-within { border-color:${NAVY}; }
.lg-search input { flex:1; min-width:0; background:none; border:none; outline:none;
  padding:10px 12px; font-size:13px; color:${L.ink}; }
.lg-search input::placeholder { color:${L.faint}; }

/* ── the rail: category chips, flat and quiet ── */
.lg-chips { display:flex; gap:7px; overflow-x:auto; scrollbar-width:none; padding:2px 2px 10px; }
.lg-chip-cat { flex-shrink:0; display:flex; align-items:center; gap:8px; white-space:nowrap;
  padding:9px 15px; cursor:pointer; font-size:12.5px; font-weight:600; color:${L.inkSoft};
  background:${L.sheet}; border:1px solid ${L.line}; border-radius:6px;
  transition: background .16s, color .16s, border-color .16s; }
.lg-chip-cat:hover { border-color:${L.alu}; color:${L.ink}; }
.lg-chip-cat.on { background:${NAVY}; border-color:${NAVY}; color:#fff; }
.lg-chip-cat .lg-led { width:5px; height:5px; border-radius:50%; background:${L.alu}; flex-shrink:0; }
.lg-chip-cat.on .lg-led { background:${ORANGE}; animation: lgBlink 1.8s ease-in-out infinite; }

/* ── 🏷 THE TAG — looped over the photo, carrying the one fact
      that decides the purchase: cabin or hold. ── */
.lg-card { background:${L.sheet}; border:1px solid ${L.line}; border-radius:10px;
  overflow:hidden; cursor:pointer; display:flex; flex-direction:column;
  animation: lgIn .26s ease both; transition: border-color .2s, transform .2s, box-shadow .2s; }
.lg-card:hover { transform: translateY(-4px); }

.lg-shot { position:relative; width:100%; aspect-ratio:1/1; overflow:hidden;
  display:flex; align-items:center; justify-content:center;
  background:
    repeating-linear-gradient(90deg, ${L.sheet} 0 21px, ${L.sheet2} 21px 24px);
  border-bottom:1px solid ${L.lineSoft}; }
.lg-shot img { width:74%; height:74%; object-fit:contain;
  filter: drop-shadow(0 10px 16px rgba(22,41,61,.18)); transition: transform .28s ease; }
.lg-card:hover .lg-shot img { transform: scale(1.05); }

.lg-tag { position:absolute; z-index:3; top:0; left:14px; transform-origin: 50% 0;
  padding:15px 10px 7px; background:${L.sheet}; border:1px solid ${L.line}; border-top:none;
  border-radius:0 0 7px 7px; box-shadow: 0 3px 9px rgba(22,41,61,.12);
  font-size:9px; font-weight:700; letter-spacing:.16em; color:${NAVY}; }
.lg-tag::before { content:""; position:absolute; top:5px; left:50%; margin-left:-3px;
  width:6px; height:6px; border-radius:50%; background:${L.paper};
  box-shadow: inset 0 0 0 1px ${L.alu}; }
.lg-tag.hold { color:${L.muted}; }
.lg-card:hover .lg-tag { animation: lgSwing 1.5s ease-in-out infinite; }

.lg-flag { position:absolute; z-index:3; top:10px; right:10px; font-size:9.5px; font-weight:700;
  letter-spacing:.04em; padding:3px 8px; border-radius:5px; background:${ORANGE}; color:#fff; }

.lg-pill { display:inline-flex; align-items:center; gap:4px; font-size:9.5px; font-weight:600;
  color:${L.muted}; background:${L.sheet2}; border:1px solid ${L.line};
  border-radius:5px; padding:3px 8px; white-space:nowrap; }
.lg-pill.key { color:${NAVY}; background:${L.orangeSoft}; border-color:${L.orangeEdge}; }

.lg-add { width:100%; height:40px; border:1px solid; border-radius:6px; background:transparent;
  display:flex; align-items:center; justify-content:center; gap:7px; cursor:pointer;
  font-size:12px; font-weight:700; transition: background .16s, color .16s; }
.lg-step { display:flex; align-items:center; justify-content:space-between;
  height:40px; border:1px solid; border-radius:6px; padding:0 5px; }
.lg-step button { width:28px; height:28px; border-radius:5px; border:none; cursor:pointer;
  display:flex; align-items:center; justify-content:center; }

.lg-panel { background:${L.sheet}; border:1px solid ${L.line}; border-radius:10px; }
.lg-node { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:6px;
  cursor:pointer; font-size:13px; color:${L.inkSoft}; transition: background .14s, color .14s; }
.lg-node:hover { background:${L.sheet2}; color:${L.ink}; }
.lg-node.on { background:${NAVY}; color:#fff; }

/* ── shop by category ── */
.lg-scg { background:${L.sheet}; border:1px solid ${L.line}; border-radius:12px;
  overflow:hidden; transition: border-color .2s, transform .2s, box-shadow .2s; }
.lg-scg:hover { border-color:${L.alu}; transform: translateY(-3px);
  box-shadow: 0 14px 32px rgba(22,41,61,.10); }
.lg-scg-tile { background:none; border:none; cursor:pointer; padding:0; display:flex; flex-direction:column; }
.lg-scg-face { width:100%; aspect-ratio:1/1; border-radius:8px; overflow:hidden; position:relative;
  background:${L.sheet2}; border:1px solid ${L.lineSoft}; transition: outline .15s; }

.lg-tab { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px;
  font-weight:600; cursor:pointer; color:${L.faint}; padding:4px 10px; background:none; border:none; }
.lg-tab.on { color:${NAVY}; }
.lg-nav { display:flex; align-items:center; gap:11px; padding:12px 16px; cursor:pointer;
  font-size:13.5px; font-weight:600; color:${L.inkSoft}; background:none; width:100%;
  text-align:left; border:none; border-bottom:1px solid ${L.line}; }
.lg-nav:hover { background:${L.sheet2}; color:${L.ink}; }

@media (prefers-reduced-motion: reduce) {
  .lg-card, .lg-card:hover .lg-tag, .lg-chip-cat.on .lg-led { animation: none !important; }
  .lg-card, .lg-shot img, .lg-scg, .lg-chip-cat { transition: none; }
}
`;

const S: Record<string, React.CSSProperties> = {
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(228px,1fr))", gap: 16 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  tabs: {
    position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 62,
    background: "rgba(255,255,255,.96)", backdropFilter: "blur(12px)",
    borderTop: `1px solid ${L.line}`, display: "flex",
    justifyContent: "space-around", alignItems: "center", zIndex: 150,
  },
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const LuggageStore: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const filteredListings = useSelector((s: RootState) => s.storeProducts.filteredProducts);
  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector((s: RootState) => (s as any).storeProductRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* the category is the master toggle — this page is one department */
  const [activeCat, setActiveCat] = useState<string>(LUGGAGE_CATEGORIES[0]?.id ?? LUGGAGE_DEPT_ID);
  const cat = useMemo(
    () => LUGGAGE_CATEGORIES.find((c) => c.id === activeCat) ?? LUGGAGE_CATEGORIES[0],
    [activeCat]
  );
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  /* luggage's own filter: the only question that matters at the gate */
  const [fit, setFit] = useState<FitKey>("all");

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [qty, setQty] = useState(1);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country) as any);
                }
              });
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName })
        .then((r) => { r.onchange = () => { if (r.state === "granted") detect(); }; })
        .catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  useEffect(() => {
    if (!detailItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailItem]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSub(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveSub(null); setActiveDeep(null); setActiveSort("default");
    setSearchQuery(""); setFit("all");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string) => {
    setActiveCat(id); setActiveSub(null); setActiveDeep(null); setOpenNodes(new Set());
  };
  const handleSubSelect = (id: string) => { setActiveSub((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  /** Shop-by-category tiles can sit at either level — route by parent. */
  const handleTileSelect = (node: CategoryItem) => {
    if (node.parentId === LUGGAGE_DEPT_ID) handleCatSelect(node.id);
    else if (node.parentId === activeCat) handleSubSelect(node.id);
    else handleDeepSelect(node.id);
  };
  const isTileSelected = (node: CategoryItem) =>
    (node.parentId === LUGGAGE_DEPT_ID && activeCat === node.id) ||
    activeSub === node.id || activeDeep === node.id;

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const openDetail = (item: any) => { setDetailItem(item); setQty(1); setSelectedVariants({}); };
  const closeDetail = () => setDetailItem(null);

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (hasVariants(item)) { openDetail(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;
    const vDefs = variantAttrsForItem(detailItem);
    if (vDefs.some((a: any) => selectedVariants[String(a.id)] == null)) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart({
      listingId: detailItem.listingId, name: detailItem.name!,
      rate: detailItem.rate!, qty,
      photo: detailItem.photos?.[0],
      hostId: detailItem.hostId, brandId: detailItem.brandId,
      selectedVariants: { ...selectedVariants },
    } as any));
    closeDetail(); setQty(1); setSelectedVariants({});
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToCheckout = () => navigate(`/pay/${userId}`, { state: { items: cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/brand/${id}`); };
  const handleLogout = async () => { await dispatch(logout() as any); navigate("/AuthSwitch"); };

  /* ═══════════════════════════════════
     FILTERING — always inside Luggage
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings || []).filter((l: any) => {
      if (!l.isPublished) return false;
      if (isOutOfStock(l)) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(LUGGAGE_DEPT_ID)) return false;
      if (!chain.includes(activeCat)) return false;
      if (activeSub && !chain.includes(activeSub)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (fit !== "all" && cabinClassOf(l) !== fit) return false;
      if (q) {
        const factText = factsOf(l).map((f) => `${f.name} ${f.value}`).join(" ");
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.vendor ?? ""} ${(l.tags ?? []).join(" ")} ${factText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [filteredListings, activeCat, activeSub, activeDeep, searchQuery, activeSort, fit]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));
    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeSub ? catName(activeSub) : null;
  const catChildren = useMemo(() => childrenOf(activeCat), [activeCat]);

  /* ═══════════════════════════════════
     HEADER  (plain function calls so the search keeps focus)
  ═══════════════════════════════════ */
  const headerBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: isMobile ? "11px 14px" : "13px 22px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ width: 30, height: 30, borderRadius: 7, background: NAVY, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Luggage size={15} color="#fff" />
        </div>
        {!isMobile && <span className="lg-disp" style={{ fontSize: 19, color: L.ink }}>Luggage</span>}
      </div>

      {locationDenied ? (
        <button className="lg-hbtn" onClick={() => setLocationModalOpen(true)} aria-label="Location off">
          <AlertTriangle size={13} />
        </button>
      ) : !userCountry ? (
        <div className="lg-hbtn">
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: ORANGE, animation: "lgBlink 1.2s infinite" }} />
          {!isMobile && <span>Locating…</span>}
        </div>
      ) : (
        <button onClick={handleToggleGlobal} className="lg-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          {!isMobile && <span>{showGlobal ? "Global" : userCity || userCountry}</span>}
        </button>
      )}

      <div className="lg-search">
        <span style={{ padding: "0 4px 0 11px", display: "flex" }}><Search size={14} color={L.faint} /></span>
        <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search cases, backpacks, packing cubes…" aria-label="Search luggage" />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 10px", color: L.faint, display: "flex" }}>
            <X size={13} />
          </button>
        )}
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="lg-hbtn">
        <LayoutGrid size={14} />{!isMobile && <span>Browse</span>}
      </button>

      <button onClick={goToCheckout} className="lg-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={14} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal}` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span className="lg-mono" style={{ position: "absolute", top: -6, right: -6, background: ORANGE, color: "#fff", fontSize: 9, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="lg-hbtn" aria-label="Profile"><User size={14} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 196, background: L.sheet, border: `1px solid ${L.line}`, borderRadius: 10, overflow: "hidden", zIndex: 400, boxShadow: "0 18px 44px rgba(22,41,61,.16)" }}>
                <button className="lg-nav" onClick={() => navigate(`/profile/${userId}`)}><User size={14} /> Profile</button>
                <button className="lg-nav" onClick={() => navigate(`/clientdashboard/${userId}`)}><ShoppingBag size={14} /> Orders</button>
                <button className="lg-nav" style={{ borderBottom: "none", color: L.orange }} onClick={handleLogout}><LogOut size={14} /> Sign out</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     CATEGORY RAIL
  ═══════════════════════════════════ */
  const categoryRail = () => (
    <div className="lg-chips">
      {LUGGAGE_CATEGORIES.map((c) => {
        const on = c.id === activeCat;
        return (
          <button key={c.id} className={`lg-chip-cat${on ? " on" : ""}`} onClick={() => handleCatSelect(c.id)}>
            <span className="lg-led" />
            <span style={{ display: "flex", opacity: on ? 1 : .7 }}>{c.icon}</span>
            {c.name}
          </button>
        );
      })}
    </div>
  );

  const heading = () => (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", padding: "4px 2px 14px" }}>
      <div>
        <div className="lg-mono" style={{ fontSize: 10, letterSpacing: ".22em", color: L.faint, textTransform: "uppercase", marginBottom: 4 }}>
          Luggage / {cat?.name ?? "All"}
        </div>
        <h1 className="lg-disp" style={{ fontSize: isMobile ? 34 : 48, color: L.ink, lineHeight: .95 }}>
          {activeFilterName ?? cat?.name ?? "Luggage"}
        </h1>
      </div>
      <div className="lg-mono" style={{ fontSize: 11, color: L.muted, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span><b style={{ color: L.ink }}>{visibleCount}</b> pieces</span>
        <span style={{ color: L.line }}>│</span>
        <span><b style={{ color: L.ink }}>{productsByBrand.length}</b> sellers</span>
        <span style={{ color: L.line }}>│</span>
        <span>{locationDenied ? "worldwide" : showGlobal ? "global" : userCity ?? userCountry ?? "nearby"}</span>
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     FIT FILTER — cabin or hold, the gate question
  ═══════════════════════════════════ */
  const fitBar = () => {
    const opts: { key: FitKey; label: string; icon: React.ReactNode }[] = [
      { key: "all", label: "All sizes", icon: <CircleDot size={12} /> },
      { key: "cabin", label: "Cabin", icon: <Plane size={12} /> },
      { key: "hold", label: "Hold", icon: <Luggage size={12} /> },
    ];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
        {opts.map((o) => {
          const on = fit === o.key;
          return (
            <button key={o.key} onClick={() => setFit(o.key)}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 6,
                border: `1px solid ${on ? NAVY : L.line}`, background: on ? NAVY : L.sheet,
                color: on ? "#fff" : L.muted, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
              }}>
              {o.icon}{o.label}
            </button>
          );
        })}
        <span className="lg-mono" style={{ fontSize: 10, color: L.faint, marginLeft: 4 }}>
          cabin = fits {CABIN_LIMIT.long}×{CABIN_LIMIT.wide}×{CABIN_LIMIT.deep} cm
        </span>
      </div>
    );
  };

  /* ═══════════════════════════════════
     CATEGORY ICON BAR
  ═══════════════════════════════════ */
  const categoryIconBar = () => {
    if (!LUGGAGE_CATEGORIES.length) return null;
    return (
      <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 22px", background: L.sheet, borderRadius: 12, border: `1px solid ${L.line}` }}>
        <div style={{ display: "flex", gap: isMobile ? 16 : 24, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any }}>
          {LUGGAGE_CATEGORIES.map((node) => {
            const on = node.id === activeCat;
            return (
              <button key={node.id} onClick={() => handleCatSelect(node.id)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 7, padding: "2px 2px" }}>
                <div style={{
                  width: isMobile ? 48 : 56, height: isMobile ? 48 : 56, borderRadius: "50%", overflow: "hidden",
                  border: `2px solid ${on ? NAVY : L.line}`,
                  boxShadow: on ? `0 0 0 3px ${L.orangeSoft}` : "none", transition: "all .2s",
                }}>
                  {node.image ? (
                    <img src={node.image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: on ? 1 : .85 }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: on ? NAVY : L.sheet2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: on ? "#fff" : L.muted, display: "flex", fontSize: isMobile ? 18 : 21 }}>{node.icon}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10.5, fontWeight: on ? 700 : 500, color: on ? L.ink : L.muted, whiteSpace: "nowrap" }}>{node.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — 4-up tile cards.
     Tiles SELECT a filter, never navigate. When the tree below the
     focus node is empty (most of Luggage today) it falls back to
     the department's own categories so the block still works.
  ═══════════════════════════════════ */
  const shopByCategory = () => {
    const focusId = activeSub ?? activeCat;
    const focusKids = childrenOf(focusId);
    const usingFallback = focusKids.length === 0;
    const tileSource = usingFallback ? LUGGAGE_CATEGORIES : focusKids;
    if (!tileSource.length) return null;

    const chunk4 = (arr: CategoryItem[]) => {
      const out: CategoryItem[][] = [];
      for (let i = 0; i < arr.length; i += 4) out.push(arr.slice(i, i + 4));
      return out;
    };
    const focusName = usingFallback ? "All Luggage" : (catName(focusId) || cat?.name || "All");
    const chunks = chunk4(tileSource);
    const cardGroups = chunks.map((tiles, idx) => ({
      title: chunks.length > 1 ? `${focusName} ${idx + 1}` : focusName,
      tiles,
    }));

    return (
      <div style={{ marginBottom: 28 }}>
        <div onClick={() => setShopCatExpanded((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <span className="lg-disp" style={{ fontSize: isMobile ? 19 : 22, color: L.ink }}>Shop by Category</span>
          <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: .2 }} style={{ display: "flex" }}>
            <ChevronDown size={18} color={L.muted} />
          </motion.div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div key={focusId} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: .25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group, gi) => (
                  <div key={`${group.title}-${gi}`} className="lg-scg"
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}) }}>
                    <div className="lg-disp" style={{ padding: isMobile ? "11px 12px 7px" : "14px 16px 9px", fontSize: isMobile ? 14 : 16, color: L.ink }}>
                      {group.title}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 6 : 8, padding: isMobile ? "0 9px 9px" : "0 10px 10px" }}>
                      {group.tiles.map((node: CategoryItem) => {
                        const sel = isTileSelected(node);
                        const hasKids = childrenOf(node.id).length > 0;
                        return (
                          <button key={node.id} className="lg-scg-tile" onClick={() => handleTileSelect(node)}>
                            <div className="lg-scg-face" style={{ outline: sel ? `2px solid ${NAVY}` : "none", outlineOffset: 2 }}>
                              {node.image ? (
                                <>
                                  <img src={node.image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${NAVY}E6, transparent 58%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 6 : 8, left: 5, right: 5, textAlign: "center", fontSize: isMobile ? 9.5 : 11, fontWeight: 700, color: "#fff", lineHeight: 1.15 }}>{node.name}</span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 7, padding: 6 }}>
                                  <span style={{ fontSize: isMobile ? 21 : 25, color: sel ? NAVY : L.muted, display: "flex" }}>{node.icon}</span>
                                  <span style={{ fontSize: isMobile ? 9.5 : 11, fontWeight: 600, color: L.inkSoft, textAlign: "center", lineHeight: 1.15 }}>{node.name}</span>
                                </div>
                              )}
                              {sel ? (
                                <div style={{ position: "absolute", top: 6, right: 6, width: 18, height: 18, borderRadius: "50%", background: NAVY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              ) : hasKids ? (
                                <div style={{ position: "absolute", top: 6, right: 6, width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,.92)", border: `1px solid ${L.line}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ChevronRight size={10} color={NAVY} />
                                </div>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BROWSE TREE
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const picked = activeSub === node.id || activeDeep === node.id;
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        <div className={`lg-node${picked ? " on" : ""}`}
          onClick={() => {
            if (depth === 0) handleSubSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}>
          <span style={{ display: "flex", color: picked ? ORANGE : L.faint, flexShrink: 0 }}>{node.icon}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
          {kids.length > 0 && (
            <motion.span animate={{ rotate: isOpen ? 90 : 0 }} style={{ display: "flex", flexShrink: 0 }}>
              <ChevronRight size={13} color={picked ? ORANGE : L.faint} />
            </motion.span>
          )}
          {picked && kids.length === 0 && <Check size={13} color={ORANGE} style={{ flexShrink: 0 }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={{ borderLeft: `1px solid ${L.line}`, marginLeft: 12, paddingLeft: 4 }}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const browsePanel = () => (
    <div className="lg-panel" style={{ padding: 14 }}>
      <div className="lg-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: L.faint, textTransform: "uppercase", marginBottom: 10 }}>
        {cat?.name ?? "Browse"}
      </div>
      {catChildren.length === 0
        ? <p style={{ fontSize: 12, color: L.faint }}>No sub-categories here yet.</p>
        : catChildren.map((c) => <TreeNode key={c.id} node={c} depth={0} />)}
    </div>
  );

  const mobileBrowseSheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(22,41,61,.5)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", background: L.paper, borderTop: `1px solid ${L.line}`, borderRadius: "14px 14px 0 0", zIndex: 401, overflowY: "auto" }}>
            <div style={{ width: 34, height: 3, background: L.alu, borderRadius: 2, margin: "12px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", padding: "12px 18px 10px", borderBottom: `1px solid ${L.line}` }}>
              <span className="lg-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: L.muted }}>BROWSE</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}>
                <X size={15} color={L.muted} />
              </button>
            </div>
            <div style={{ padding: 16 }}>{categoryRail()}<div style={{ height: 12 }} />{browsePanel()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     SELLER ROW
  ═══════════════════════════════════ */
  const sellerRow = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const name = partner?.name || "Seller";
    const logo = partner?.image || appLogo;
    const accent = brandAccentColor(partner);
    const city = partner?.address?.city;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 30, height: 30, borderRadius: 7, overflow: "hidden", flexShrink: 0, border: `1px solid ${L.line}`, background: L.sheet, padding: 0, cursor: "pointer" }}>
          <img src={logo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)} className="lg-disp"
          style={{ fontSize: 17, color: L.ink, cursor: "pointer", borderBottom: `2px solid ${accent}`, paddingBottom: 1 }}>
          {name}
        </span>
        {city && <span className="lg-mono" style={{ fontSize: 10.5, color: L.faint }}>{city}</span>}
        <div style={{ flex: 1, height: 1, background: L.line }} />
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${L.line}`, background: L.sheet, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={14} color={accent} />
        </button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     🏷 TAGGED CARD
  ═══════════════════════════════════ */
  const productCard = (item: any) => {
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const q = getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const klass = cabinClassOf(item);
    const spec = specLineOf(item);
    const shell = shellFactOf(item);
    const wheels = wheelsFactOf(item);
    const locked = hasLock(item);
    const configurable = hasVariants(item);
    const isWished = wished.has(item.listingId);

    return (
      <div key={item.listingId} className="lg-card" onClick={() => openDetail(item)}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = accent;
          el.style.boxShadow = `0 0 0 1px ${accent}22, 0 16px 36px rgba(22,41,61,.12)`;
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = L.line;
          el.style.boxShadow = "none";
        }}>
        <div className="lg-shot">
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name}
                onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.src = appLogo; el.style.opacity = "0.25"; }} />
            : <Package size={32} color={L.faint} />}

          {klass && (
            <span className={`lg-tag lg-mono${klass === "hold" ? " hold" : ""}`}>
              {klass === "cabin" ? "CABIN" : "HOLD"}
            </span>
          )}
          {discount && <span className="lg-flag lg-mono">−{discount}%</span>}

          <button onClick={(e) => toggleWish(item.listingId, e)} aria-label="Save"
            style={{ position: "absolute", zIndex: 3, bottom: 10, right: 10, width: 27, height: 27, borderRadius: 6, background: "rgba(255,255,255,.94)", border: `1px solid ${L.line}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Heart size={12} color={isWished ? accent : L.muted} fill={isWished ? accent : "none"} />
          </button>
        </div>

        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 7, flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: L.ink, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 35 }}>
            {item.name}
          </div>

          <div className="lg-mono" style={{ fontSize: 10.5, color: L.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {spec ?? (
              <span style={{ letterSpacing: ".06em", textTransform: "uppercase", fontSize: 9.5, color: L.faint }}>
                {catPathLabel(item.categoryId) || item.category || "luggage"}
              </span>
            )}
          </div>

          {(shell || wheels || locked) && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {shell && <span className="lg-pill key">{shell.value}</span>}
              {wheels && <span className="lg-pill"><RotateCcw size={9} />{wheels.value}</span>}
              {locked && <span className="lg-pill"><Lock size={9} />Lock</span>}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: "auto", paddingTop: 4 }}>
            <span className="lg-mono" style={{ fontSize: 17, fontWeight: 500, color: L.ink }}>{price(item.rate ?? 0)}</span>
            {discount && (
              <span className="lg-mono" style={{ fontSize: 11, color: L.faint, textDecoration: "line-through" }}>
                {price(item.compareAtPrice ?? 0)}
              </span>
            )}
            {hasRating && (
              <span className="lg-mono" style={{ marginLeft: "auto", fontSize: 10.5, color: L.muted, display: "flex", alignItems: "center", gap: 3 }}>
                <Star size={10} fill={ORANGE} color={ORANGE} />{summary.avgRating.toFixed(1)}
              </span>
            )}
          </div>

          {q > 0 && !configurable ? (
            <div className="lg-step" onClick={(e) => e.stopPropagation()} style={{ borderColor: accent }}>
              <button aria-label="Remove one" style={{ background: accent }}
                onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={13} color="#fff" />
              </button>
              <span className="lg-mono" style={{ fontSize: 13, color: accent }}>{q}</span>
              <button aria-label="Add one" style={{ background: accent }} onClick={(e) => handleAddToCart(item, e)}>
                <Plus size={13} color="#fff" />
              </button>
            </div>
          ) : (
            <button className="lg-add" onClick={(e) => handleAddToCart(item, e)}
              style={{ borderColor: accent, color: accent }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = accent; (e.currentTarget as HTMLElement).style.color = "#fff"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = accent; }}>
              {configurable ? <Sliders size={13} /> : <Plus size={14} />}
              {configurable ? "Choose options" : "Add to cart"}
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     RESULTS
  ═══════════════════════════════════ */
  const emptyState = (title: string, body: string) => (
    <div style={{ textAlign: "center", padding: "64px 20px" }}>
      <div style={{ width: 54, height: 54, borderRadius: 10, background: L.sheet, border: `1px solid ${L.line}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Luggage size={22} color={L.faint} />
      </div>
      <h3 className="lg-disp" style={{ fontSize: 22, color: L.ink, marginBottom: 8 }}>{title}</h3>
      <p style={{ color: L.muted, fontSize: 13, marginBottom: 22 }}>{body}</p>
      <button onClick={resetAllFilters} className="lg-hbtn" style={{ margin: "0 auto", padding: "10px 20px" }}>
        Clear filters
      </button>
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div className="lg-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: L.faint, textTransform: "uppercase" }}>
          {activeFilterName ?? "On the belt"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort"
            className="lg-mono"
            style={{ background: L.sheet, border: `1px solid ${L.line}`, color: L.inkSoft, borderRadius: 6, padding: "6px 10px", fontSize: 11, cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal} className="lg-hbtn">
              {showGlobal ? <MapPin size={12} /> : <Globe size={12} />}
              {showGlobal ? "Local" : "Global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0
          ? emptyState(
              fit !== "all"
                ? `No ${fit === "cabin" ? "cabin-sized" : "hold"} bags here`
                : activeFilterName ? `No ${activeFilterName} in stock` : `Nothing in ${cat?.name ?? "this category"} yet`,
              fit !== "all"
                ? "Switch back to All sizes, or try another category."
                : "Pick another category above, or clear the filters."
            )
          : locationDenied
            ? emptyState("Location is off", "Turn it on to see sellers who can ship to you.")
            : emptyState(
                showGlobal ? "No global sellers here" : "No local sellers here",
                `Switch to ${showGlobal ? "local" : "global"} above, or clear the filters.`
              )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {sellerRow(group.brandId)}
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => productCard(item))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     DETAIL
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;
    const item = detailItem;
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const brandName = partner?.name;
    const img = item.photos?.[0];
    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;

    const variantDefs = variantAttrsForItem(item);
    const facts = factsOf(item);
    const klass = cabinClassOf(item);
    const spec = specLineOf(item);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const allChosen = variantDefs.every((a: any) => selectedVariants[String(a.id)] != null);
    const canContinue = !soldOut && qty > 0 && qty <= stock && allChosen;

    const content = (
      <div style={{ background: L.paper, border: `1px solid ${L.line}`, borderRadius: isMobile ? "14px 14px 0 0" : 14, overflow: "hidden", width: "100%", maxWidth: isMobile ? "100%" : 480, maxHeight: isMobile ? "88vh" : "86vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 3, background: accent, flexShrink: 0 }} />
        {isMobile && <div style={{ width: 34, height: 3, borderRadius: 2, background: L.alu, margin: "10px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "16px 18px 0" }}>
          <div className="lg-shot" style={{ borderRadius: 10, border: `1px solid ${L.line}`, marginBottom: 14 }}>
            {img
              ? <img src={img} alt={item.name} />
              : <Package size={40} color={L.faint} />}
            {klass && (
              <span className={`lg-tag lg-mono${klass === "hold" ? " hold" : ""}`}>
                {klass === "cabin" ? "CABIN" : "HOLD"}
              </span>
            )}
            <button onClick={closeDetail} aria-label="Close"
              style={{ position: "absolute", zIndex: 3, top: 10, right: 10, width: 28, height: 28, borderRadius: 6, background: "rgba(255,255,255,.94)", border: `1px solid ${L.line}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <X size={14} color={L.inkSoft} />
            </button>
          </div>

          {brandName && (
            <p className="lg-mono" style={{ fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase", color: accent, marginBottom: 4 }}>{brandName}</p>
          )}
          <div className="lg-disp" style={{ fontSize: 24, color: L.ink, marginBottom: 4, lineHeight: 1.05 }}>{item.name}</div>
          {item.categoryId && (
            <p className="lg-mono" style={{ fontSize: 10.5, color: L.faint, marginBottom: 12 }}>{catPathLabel(item.categoryId)}</p>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span className="lg-mono" style={{ fontSize: 22, fontWeight: 500, color: L.ink }}>{price(item.rate ?? 0)}</span>
            {discount && <span className="lg-mono" style={{ fontSize: 12, color: L.faint, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>}
            {hasRating && (
              <span className="lg-mono" style={{ fontSize: 11, color: L.muted, display: "flex", alignItems: "center", gap: 4 }}>
                <Star size={11} fill={ORANGE} color={ORANGE} />
                {summary.avgRating.toFixed(1)} ({summary.totalReviews.toLocaleString()})
              </span>
            )}
          </div>

          {/* ── the cabin check, with its working shown ── */}
          {klass && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 13px", borderRadius: 8, background: klass === "cabin" ? L.orangeSoft : L.sheet, border: `1px solid ${klass === "cabin" ? L.orangeEdge : L.line}`, marginBottom: 16 }}>
              {klass === "cabin" ? <Plane size={15} color={ORANGE} /> : <Luggage size={15} color={L.muted} />}
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: L.ink, marginBottom: 2 }}>
                  {klass === "cabin" ? "Fits most cabin racks" : "Goes in the hold"}
                </div>
                <div className="lg-mono" style={{ fontSize: 10.5, color: L.muted, lineHeight: 1.5 }}>
                  {spec ? `${spec} · ` : ""}checked against {CABIN_LIMIT.long}×{CABIN_LIMIT.wide}×{CABIN_LIMIT.deep} cm. Confirm with your airline.
                </div>
              </div>
            </div>
          )}

          {/* ── options the seller offers ── */}
          {variantDefs.map((attr: any) => {
            const key = String(attr.id);
            const chosen = selectedVariants[key];
            const isColour = attr.inputType === "color";
            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 9 }}>
                  <span className="lg-mono" style={{ fontSize: 10, letterSpacing: ".18em", color: L.muted, textTransform: "uppercase" }}>{attr.name}</span>
                  {chosen == null && <span style={{ fontSize: 10, fontWeight: 600, color: ORANGE }}>pick one</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {attr.options.map((opt: any) => {
                    const on = String(chosen) === String(opt.id);
                    return (
                      <button key={opt.id} onClick={() => setSelectedVariants((p) => ({ ...p, [key]: opt.id }))}
                        style={{
                          display: "flex", alignItems: "center", gap: 7, padding: isColour ? "6px 12px 6px 7px" : "8px 13px",
                          borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600,
                          border: `1px solid ${on ? NAVY : L.line}`,
                          background: on ? L.sheet2 : L.sheet, color: on ? L.ink : L.inkSoft,
                          outline: on ? `1px solid ${NAVY}` : "none",
                        }}>
                        {isColour && (
                          <span style={{ width: 16, height: 16, borderRadius: "50%", background: opt.hex || L.sheet2, border: "1px solid rgba(22,41,61,.15)" }} />
                        )}
                        {opt.name || opt.standard || String(opt.id)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── the spec sheet ── */}
          {facts.length > 0 && (
            <div style={{ border: `1px solid ${L.line}`, borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
              <div className="lg-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: L.muted, textTransform: "uppercase", padding: "10px 13px", borderBottom: `1px solid ${L.line}`, background: L.sheet2 }}>
                Specifications
              </div>
              {facts.map((f, i) => (
                <div key={f.name} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "9px 13px", background: i % 2 ? L.sheet : L.paper, fontSize: 12 }}>
                  <span style={{ color: L.muted }}>{f.name}</span>
                  <span className="lg-mono" style={{ color: L.ink, textAlign: "right" }}>{f.value}</span>
                </div>
              ))}
            </div>
          )}

          {item.vendor && (
            <p style={{ fontSize: 11.5, color: L.muted, marginBottom: 10 }}>
              <span style={{ color: L.inkSoft, fontWeight: 700 }}>Vendor:</span> {item.vendor}
            </p>
          )}
          {item.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {item.tags.map((t: string) => (
                <span key={t} className="lg-mono" style={{ fontSize: 10, color: L.muted, background: L.sheet, border: `1px solid ${L.line}`, borderRadius: 5, padding: "3px 8px" }}>#{t}</span>
              ))}
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: L.inkSoft, lineHeight: 1.7, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {soldOut ? (
            <p className="lg-mono" style={{ fontSize: 11, color: L.faint, marginBottom: 12 }}>OUT OF STOCK</p>
          ) : stock <= 5 ? (
            <p className="lg-mono" style={{ fontSize: 11, color: ORANGE, marginBottom: 12 }}>ONLY {stock} LEFT</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <span className="lg-mono" style={{ fontSize: 10, letterSpacing: ".18em", color: L.muted }}>QTY</span>
              <div style={{ display: "flex", border: `1px solid ${L.line}`, borderRadius: 6, overflow: "hidden" }}>
                <button onClick={() => setQty((v) => Math.max(1, v - 1))} aria-label="Decrease"
                  style={{ width: 38, height: 38, border: "none", background: L.sheet, cursor: "pointer", color: L.inkSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={13} />
                </button>
                <div className="lg-mono" style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: L.ink, background: L.sheet2 }}>{qty}</div>
                <button onClick={() => setQty((v) => Math.min(stock || 1, v + 1))} disabled={qty >= stock} aria-label="Increase"
                  style={{ width: 38, height: 38, border: "none", background: L.sheet, cursor: qty >= stock ? "not-allowed" : "pointer", color: L.inkSoft, opacity: qty >= stock ? .4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={13} />
                </button>
              </div>
              <span className="lg-mono" style={{ fontSize: 12, color: L.muted, marginLeft: "auto" }}>
                {price((item.rate ?? 0) * qty)}
              </span>
            </div>
          )}
        </div>

        <div style={{ padding: "13px 18px 18px", display: "flex", gap: 9, flexShrink: 0, borderTop: `1px solid ${L.line}` }}>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(false)}
            style={{ flex: 1, padding: "12px 0", borderRadius: 6, border: `1px solid ${canContinue ? accent : L.line}`, background: "transparent", color: canContinue ? accent : L.faint, fontWeight: 700, fontSize: 12.5, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Add to cart
          </button>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(true)}
            style={{ flex: 1.15, padding: "12px 0", borderRadius: 6, border: "none", background: canContinue ? accent : L.lineSoft, color: canContinue ? "#fff" : L.faint, fontWeight: 700, fontSize: 12.5, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(22,41,61,.55)", backdropFilter: "blur(5px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: .96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .96, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 480, maxWidth: "100%" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(22,41,61,.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ scale: .94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .94, y: 20 }} onClick={(e) => e.stopPropagation()}
            className="lg-panel" style={{ width: 320, padding: 22, textAlign: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: L.sheet2, border: `1px solid ${L.line}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <MapPin size={19} color={NAVY} />
            </div>
            <h3 className="lg-disp" style={{ fontSize: 20, color: L.ink, marginBottom: 8 }}>Location is off</h3>
            <p style={{ color: L.muted, fontSize: 12.5, lineHeight: 1.6, marginBottom: 18 }}>
              Allow location in your browser settings to see sellers who can ship to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)} className="lg-hbtn" style={{ margin: "0 auto", padding: "10px 22px" }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const bottomTabs = () => (isMobile && userId ? (
    <div style={S.tabs}>
      {[
        { icon: <Home size={20} />, label: "Home", on: false, go: () => navigate(`/StoreProductsHome/${userId}`) },
        { icon: <Luggage size={20} />, label: "Luggage", on: true, go: () => navigate("/LuggageStore") },
        {
          label: "Cart", on: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={20} />
              {cartTotal > 0 && (
                <span className="lg-mono" style={{ position: "absolute", top: -5, right: -7, background: ORANGE, color: "#fff", fontSize: 8, minWidth: 15, height: 15, padding: "0 3px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>
              )}
            </div>
          ), go: goToCheckout,
        },
        { icon: <ShoppingBasket size={20} />, label: "Sellers", on: false, go: () => navigate("/Stores") },
        { icon: <User size={20} />, label: "Profile", on: false, go: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <button key={t.label} onClick={t.go} className={`lg-tab${t.on ? " on" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.on ? NAVY : L.faint })}
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div className="lg">
      <style>{GLOBAL_CSS}</style>

      <header className="lg-rail">
        <div className="lg-edge" />
        {headerBar()}
        <div style={{ padding: isMobile ? "0 14px" : "0 22px" }}>{!isMobile && categoryRail()}</div>
      </header>

      {mobileBrowseSheet()}

      <div style={{ maxWidth: 1320, margin: "0 auto", padding: isMobile ? "14px 14px 0" : "22px 22px 0" }}>
        {isMobile && <div style={{ marginBottom: 6 }}>{categoryRail()}</div>}
        {heading()}
        {fitBar()}

        <div style={{ display: "grid", gridTemplateColumns: !isMobile && desktopCatOpen ? "252px 1fr" : "1fr", gap: 20, alignItems: "start" }}>
          {!isMobile && desktopCatOpen && (
            <aside style={{ position: "sticky", top: 132, maxHeight: "calc(100vh - 154px)", overflowY: "auto" }}>
              {browsePanel()}
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            {categoryIconBar()}
            {shopByCategory()}
            {renderProducts(isMobile)}
          </main>
        </div>
      </div>

      {detailModal()}
      {locationModal()}

    </div>
  );
};

export default LuggageStore;
